import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmt } from "@/lib/api";

/**
 * Period Close Checklist tile — primary operational card on the Hub.
 *
 * Read-only aggregation surface (Report 50). Per Controller direction:
 *  · Active in_progress period only (no historical)
 *  · Manual refresh (no polling)
 *  · Score is secondary; blockers / next-blocker / projected verdict primary
 *  · Sections A (status) · B (blockers, MOST prominent) · C (reconciliation) · D (cert outlook)
 *  · NEXT BLOCKER is the single most actionable field
 */
export default function PeriodCloseChecklistTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.getActiveCloseChecklist()
      .then(d => { setData(d); setRefreshedAt(new Date()); setLoading(false); })
      .catch(e => { setError(e?.message || "Failed to load"); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <Shell><div className="muted" data-testid="checklist-loading">Loading…</div></Shell>;
  }
  if (error) {
    return <Shell><div data-testid="checklist-error" style={{ color: "var(--blocker)" }}>{error}</div></Shell>;
  }
  if (!data) {
    return (
      <Shell title="Period Close Checklist" testid="checklist-empty">
        <NoActivePeriod />
      </Shell>
    );
  }

  const projectedClass =
    data.projected_verdict === "CERTIFIED" ? "ok" :
    data.projected_verdict === "CERTIFIED WITH EXCEPTIONS" ? "ok" : "blocker";

  return (
    <Shell testid="checklist-tile">
      {/* ─── Header: title + refresh ──────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div>
          <div className="heading h2" data-testid="checklist-title">Period Close Checklist</div>
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            What remains before this period can be certified and locked
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="muted" style={{ fontSize: 11 }} data-testid="checklist-refreshed-at">
            Last refreshed: {refreshedAt ? refreshedAt.toLocaleTimeString() : "—"}
          </div>
          <button
            className="btn"
            onClick={load}
            style={{ marginTop: 4, fontSize: 11, padding: "2px 10px" }}
            data-testid="checklist-refresh"
          >Refresh</button>
        </div>
      </div>

      {/* ─── NEXT BLOCKER · primary actionable field ───────────────────── */}
      <div
        data-testid="checklist-next-blocker"
        style={{
          padding: "12px 16px",
          background: data.blocking_items_count > 0 ? "var(--blocker-soft, #fff5ee)" : "var(--ok-soft, #eef8f0)",
          border: "1px solid " + (data.blocking_items_count > 0 ? "var(--blocker, #d63845)" : "var(--ok, #2e7d4f)"),
          borderRadius: 4,
          marginBottom: 14,
        }}
      >
        <div className="muted" style={{
          fontSize: 10, fontWeight: 600, textTransform: "uppercase",
          letterSpacing: "0.06em", marginBottom: 2,
        }}>Next blocker</div>
        <div style={{ fontSize: 15, fontWeight: 600 }} data-testid="checklist-next-blocker-text">
          {data.next_blocker}
        </div>
      </div>

      {/* ─── Section grid ──────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1.4fr 1fr 1.1fr", gap: 16 }}>
        {/* Section A — Current Status */}
        <Section title="Current Status" testid="section-a">
          <KV k="Active Period" v={data.period_label} testid="kv-active-period" />
          <KV k="Period Status"
              v={<span className="mono" style={{ fontSize: 12 }}>{data.period_status}</span>}
              testid="kv-period-status" />
          {/* Score is secondary — small, single-line, no bar */}
          <KV
            k="Readiness"
            v={<span className="mono" style={{ fontSize: 12 }} data-testid="kv-readiness">
              {data.readiness_score}/100 · <span className="muted">{data.readiness_state}</span>
            </span>}
            testid="kv-readiness-row"
          />
        </Section>

        {/* Section B — Blocking Items (MOST IMPORTANT) */}
        <Section title="Blocking Items" testid="section-b" emphasis>
          <KV k="Under Review" v={<Counter n={data.under_review_count} blocker={data.under_review_count > 0} />} testid="kv-under-review" />
          <KV k="Draft Journals" v={<Counter n={data.draft_journal_count} blocker={data.draft_journal_count > 0} />} testid="kv-drafts" />
          <KV k="Open Match Groups" v={<Counter n={data.open_match_groups_count} blocker={data.open_match_groups_count > 0} />} testid="kv-open-mgs" />
          <KV
            k="Total Blockers"
            v={<span className="mono" data-testid="kv-blocker-count"
                     style={{ fontWeight: 700, color: data.blocking_items_count > 0 ? "var(--blocker)" : "var(--ok, #2e7d4f)" }}>
              {data.blocking_items_count}
            </span>}
            testid="kv-blocker-count-row"
          />
        </Section>

        {/* Section C — Reconciliation */}
        <Section title="Reconciliation" testid="section-c">
          <KV k="Bank Variance"
              v={<span className="mono" data-testid="kv-bank-variance"
                       style={{ color: Math.abs(data.bank_variance) > 0.01 ? "var(--attn, #b95a00)" : "var(--ink)" }}>
                ${fmt.currency(data.bank_variance)}
              </span>}
              testid="kv-bank-variance-row" />
          <KV k="GL Variance"
              v={<span className="mono" data-testid="kv-gl-variance"
                       style={{ color: Math.abs(data.gl_variance) > 0.01 ? "var(--blocker)" : "var(--ink)" }}>
                ${fmt.currency(data.gl_variance)}
              </span>}
              testid="kv-gl-variance-row" />
          <KV k="Reconciling Items"
              v={<span className="mono" data-testid="kv-ri-count">{data.reconciling_item_count}</span>}
              testid="kv-ri-count-row" />
        </Section>

        {/* Section D — Certification Outlook */}
        <Section title="Certification Outlook" testid="section-d">
          <KV
            k="Head Basis"
            v={<span data-testid="kv-head-basis">
              {data.head_cert?.basis
                ? data.head_cert.basis.charAt(0).toUpperCase() + data.head_cert.basis.slice(1).replace(/_/g, " ")
                : <span className="muted">—</span>}
            </span>}
            testid="kv-head-basis-row"
          />
          <KV
            k="Current Verdict"
            v={data.head_cert?.verdict
              ? <span className={"tag " + (data.head_cert.verdict === "CERTIFIED WITH EXCEPTIONS" ? "ok" : data.head_cert.verdict === "CERTIFIED" ? "ok" : "blocker")}
                       data-testid="kv-current-verdict">{data.head_cert.verdict}</span>
              : <span className="muted" data-testid="kv-current-verdict">—</span>}
            testid="kv-current-verdict-row"
          />
          <KV
            k="Projected Verdict"
            v={<span className={"tag " + projectedClass} data-testid="kv-projected-verdict">
              {data.projected_verdict}
            </span>}
            testid="kv-projected-verdict-row"
          />
        </Section>
      </div>

      {/* ─── Projected-verdict "WHY" explanation (per Sean's amendment §5) ─── */}
      {data.projected_verdict_reasons && data.projected_verdict_reasons.length > 0 && (
        <div
          data-testid="checklist-projected-why"
          style={{
            marginTop: 12, padding: "8px 12px",
            background: "var(--surface-elev, #fafafa)",
            border: "1px solid var(--line)",
            borderRadius: 4, fontSize: 12,
          }}
        >
          <span className="muted" style={{
            fontWeight: 600, marginRight: 6,
            textTransform: "uppercase", letterSpacing: "0.04em", fontSize: 10,
          }}>Because</span>
          {data.projected_verdict_reasons.map((r, i) => (
            <span key={i} style={{ marginRight: 12 }}>· {r}</span>
          ))}
        </div>
      )}
    </Shell>
  );
}

// ─── Layout helpers ──────────────────────────────────────────────────────

function Shell({ children, testid }) {
  return (
    <section
      data-testid={testid || "checklist-shell"}
      style={{
        border: "1px solid var(--line)",
        borderRadius: 6,
        padding: 18,
        background: "var(--surface, #fff)",
        marginBottom: 24,
      }}
    >
      {children}
    </section>
  );
}

function Section({ title, children, testid, emphasis = false }) {
  return (
    <div
      data-testid={testid}
      style={emphasis ? {
        background: "var(--surface-elev, #fafafa)",
        padding: "10px 12px",
        borderRadius: 4,
        border: "1px solid var(--line)",
      } : {}}
    >
      <div className="muted" style={{
        fontSize: 10, fontWeight: 600, textTransform: "uppercase",
        letterSpacing: "0.06em", marginBottom: 8,
      }}>{title}</div>
      {children}
    </div>
  );
}

function KV({ k, v, testid }) {
  return (
    <div
      data-testid={testid}
      style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: 12, gap: 8 }}
    >
      <span className="muted">{k}</span>
      <span style={{ textAlign: "right" }}>{v}</span>
    </div>
  );
}

function Counter({ n, blocker }) {
  return (
    <span
      className="mono"
      style={{ fontWeight: blocker ? 700 : 500, color: blocker ? "var(--blocker)" : "var(--ink)" }}
    >{n}</span>
  );
}

/**
 * Empty-state card shown when no period is currently in progress.
 * Per the production period policy: a new period is created ONLY when the
 * Controller intentionally invokes Open New Period. We discover the carry-
 * forward target by listing the accounts; if there's exactly one production
 * account, we go straight to one-click open. Otherwise we let the Controller
 * pick which account to open.
 */
function NoActivePeriod() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState("");

  useEffect(() => {
    api.accounts().then((rows) => {
      const live = (rows || []).filter((a) => a.is_active !== false);
      setAccounts(live);
      if (live.length === 1) setSelectedAccountId(live[0].id);
    }).catch((e) => setError(e?.message || "Failed to load accounts"));
  }, []);

  async function open() {
    if (!selectedAccountId) { setError("Select an account first."); return; }
    setError(null);
    setBusy(true);
    try {
      const p = await api.openNextPeriod(selectedAccountId);
      navigate(`/periods/${p.id}`);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to open new period.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="no-active-period">
      <div className="muted" style={{ fontSize: 14, marginBottom: 4 }}>
        No active close period.
      </div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 14 }}>
        A new accounting period is created only when you intentionally begin the
        close. Carry-forward balances and materiality threshold will be applied
        automatically from the most recent locked period.
      </div>

      {accounts.length === 0 ? (
        <div className="muted" style={{ fontSize: 12 }}>Loading accounts…</div>
      ) : accounts.length === 1 ? (
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <button
            className="btn primary"
            onClick={open}
            disabled={busy}
            data-testid="open-new-period-btn"
          >
            {busy ? "Opening…" : `Open New Period for ${accounts[0].name}`}
          </button>
          <span className="muted" style={{ fontSize: 12 }}>
            Carry-forward source: most recent locked period on this account.
          </span>
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <select
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
            data-testid="open-new-period-account"
            style={{ padding: "4px 8px", fontSize: 13,
                     border: "1px solid var(--line)", borderRadius: 4 }}
          >
            <option value="">— Select account —</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          <button
            className="btn primary"
            onClick={open}
            disabled={busy || !selectedAccountId}
            data-testid="open-new-period-btn"
          >
            {busy ? "Opening…" : "Open New Period"}
          </button>
        </div>
      )}

      {error && (
        <div
          data-testid="open-new-period-error"
          style={{
            marginTop: 10, padding: "6px 10px",
            background: "#fff4f0", border: "1px solid #f3c2b3",
            color: "#8a2a0c", borderRadius: 4, fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
